import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

// Tipo de IVA habitual por pais. null = sin IVA nacional.
const COUNTRY_VAT = { ES: 21, FR: 20, US: null, CA: 5, MX: 16, AR: 21, CL: 19, CO: 19, HT: 10 };

const TEXT = {
  es: {
    title: 'IVA',
    description: 'Indica tu r\u00e9gimen de IVA para que la ganancia real sea correcta. Si no sabes cu\u00e1l es el tuyo, consulta a tu gestor.',
    general: 'R\u00e9gimen general',
    generalHint: 'El IVA que cobras no es tuyo: se resta de las ventas. Introduce los costos de productos sin IVA.',
    recargo: 'Recargo de equivalencia',
    recargoHint: 'No declaras IVA: no se resta nada. Introduce los costos de productos con el IVA y el recargo que pagas.',
    none: 'No aplica / exento',
    noneHint: 'Tus ventas no llevan IVA: no se resta nada.',
    rate: 'Tipo de IVA (%)',
    suggested: 'Tipo habitual en tu pa\u00eds: {rate} %',
    useSuggested: 'Usar {rate} %',
    noVatCountry: 'En Estados Unidos no hay IVA nacional (el sales tax se cobra aparte). Normalmente se elige \u201cNo aplica\u201d.',
    caNote: 'Es el GST federal. Seg\u00fan tu provincia puede ser m\u00e1s (HST 13\u201315 %).',
    save: 'Guardar',
    saving: 'Guardando...',
    saved: 'Guardado.',
    error: 'No se pudo guardar. Revisa el tipo de IVA.',
  },
  en: {
    title: 'VAT',
    description: 'Choose your VAT regime so your real profit is correct. If you are unsure, ask your accountant.',
    general: 'Standard VAT',
    generalHint: 'The VAT you charge is not yours: it is deducted from sales. Enter product costs without VAT.',
    recargo: 'Equivalence surcharge (Spain)',
    recargoHint: 'You do not file VAT: nothing is deducted. Enter product costs including the VAT and surcharge you pay.',
    none: 'Not applicable / exempt',
    noneHint: 'Your sales carry no VAT: nothing is deducted.',
    rate: 'VAT rate (%)',
    suggested: 'Usual rate in your country: {rate}%',
    useSuggested: 'Use {rate}%',
    noVatCountry: 'The United States has no national VAT (sales tax is charged separately). Usually you choose \u201cNot applicable\u201d.',
    caNote: 'This is the federal GST. Depending on your province it can be higher (HST 13\u201315%).',
    save: 'Save',
    saving: 'Saving...',
    saved: 'Saved.',
    error: 'Could not save. Check the VAT rate.',
  },
  fr: {
    title: 'TVA',
    description: 'Indiquez votre r\u00e9gime de TVA pour que le b\u00e9n\u00e9fice r\u00e9el soit exact. En cas de doute, demandez \u00e0 votre comptable.',
    general: 'R\u00e9gime g\u00e9n\u00e9ral',
    generalHint: 'La TVA factur\u00e9e ne vous appartient pas : elle est d\u00e9duite des ventes. Saisissez les co\u00fbts des produits hors TVA.',
    recargo: 'Recargo de equivalencia (Espagne)',
    recargoHint: 'Vous ne d\u00e9clarez pas la TVA : rien n\u2019est d\u00e9duit. Saisissez les co\u00fbts TTC, surcharge comprise.',
    none: 'Non applicable / exon\u00e9r\u00e9',
    noneHint: 'Vos ventes ne portent pas de TVA : rien n\u2019est d\u00e9duit.',
    rate: 'Taux de TVA (%)',
    suggested: 'Taux habituel dans votre pays : {rate} %',
    useSuggested: 'Utiliser {rate} %',
    noVatCountry: 'Les \u00c9tats-Unis n\u2019ont pas de TVA nationale (la sales tax est factur\u00e9e \u00e0 part). On choisit g\u00e9n\u00e9ralement \u00ab Non applicable \u00bb.',
    caNote: 'C\u2019est la TPS f\u00e9d\u00e9rale. Selon votre province, le taux peut \u00eatre plus \u00e9lev\u00e9 (TVH 13\u201315 %).',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    saved: 'Enregistr\u00e9.',
    error: 'Impossible d\u2019enregistrer. V\u00e9rifiez le taux de TVA.',
  },
};

const getText = (lang) => TEXT[(lang || 'es').slice(0, 2)] || TEXT.es;

function VatSettingsCard({ token, tenant, country: countryProp, onAuthError }) {
  const { i18n } = useTranslation();
  const tx = getText(i18n.language);
  const country = String(countryProp || tenant.default_country || 'ES').toUpperCase();
  const suggestedRate = Object.prototype.hasOwnProperty.call(COUNTRY_VAT, country) ? COUNTRY_VAT[country] : undefined;
  const [regime, setRegime] = useState(tenant.vat_regime || (suggestedRate === null ? 'none' : 'general'));
  const [rate, setRate] = useState(
    tenant.vat_rate != null ? String(Number(tenant.vat_rate)) : String(typeof suggestedRate === 'number' ? suggestedRate : 21)
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const save = async () => {
    const parsed = Number(String(rate).trim().replace(',', '.'));
    if (regime === 'general' && (!Number.isFinite(parsed) || parsed < 0 || parsed > 100)) {
      setIsError(true);
      setMessage(tx.error);
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      await axios.put(
        `${API_URL}/api/v1/tenant/vat`,
        { vat_regime: regime, vat_rate: Number.isFinite(parsed) ? parsed : 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsError(false);
      setMessage(tx.saved);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onAuthError();
        return;
      }
      const detail = err.response && err.response.data && err.response.data.detail;
      setIsError(true);
      setMessage(typeof detail === 'string' ? detail : tx.error);
    } finally {
      setSaving(false);
    }
  };

  // El recargo de equivalencia solo existe en Espana (se mantiene visible si ya estaba elegido).
  const options = [['general', tx.general, tx.generalHint]];
  if (country === 'ES' || regime === 'recargo') options.push(['recargo', tx.recargo, tx.recargoHint]);
  options.push(['none', tx.none, tx.noneHint]);

  const currentRate = Number(String(rate).trim().replace(',', '.'));

  return (
    <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
      <p className="text-white text-sm font-semibold mb-1">{tx.title}</p>
      <p className="text-muted text-xs mb-4">{tx.description}</p>
      <div className="flex flex-col gap-2 mb-4">
        {options.map(([value, label, hint]) => (
          <label
            key={value}
            className={`flex gap-3 items-start bg-surfaceAlt border rounded-lg p-3 cursor-pointer ${regime === value ? 'border-accent' : 'border-edge'}`}
          >
            <input
              type="radio"
              name="vat_regime"
              value={value}
              checked={regime === value}
              onChange={() => setRegime(value)}
              className="mt-1 accent-accent"
            />
            <span>
              <span className="text-white text-sm block">{label}</span>
              <span className="text-muted text-xs block">{hint}</span>
            </span>
          </label>
        ))}
      </div>
      {suggestedRate === null && regime !== 'none' && (
        <p className="text-muted text-xs mb-4">{tx.noVatCountry}</p>
      )}
      {regime === 'general' && (
        <div className="mb-4">
          <label className="text-muted text-xs block mb-1.5">{tx.rate}</label>
          <input
            type="text"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            className="w-24 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
          />
          {typeof suggestedRate === 'number' && (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-muted text-xs">{tx.suggested.replace('{rate}', suggestedRate)}</span>
              {currentRate !== suggestedRate && (
                <button
                  type="button"
                  onClick={() => setRate(String(suggestedRate))}
                  className="text-accent text-xs underline cursor-pointer"
                >
                  {tx.useSuggested.replace('{rate}', suggestedRate)}
                </button>
              )}
            </div>
          )}
          {country === 'CA' && <p className="text-muted text-xs mt-1.5">{tx.caNote}</p>}
        </div>
      )}
      {message && <p className={`text-xs mb-3 ${isError ? 'text-red-400' : 'text-accent'}`}>{message}</p>}
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="bg-accent text-panel font-bold text-sm px-5 py-2 rounded-lg cursor-pointer"
      >
        {saving ? tx.saving : tx.save}
      </button>
    </div>
  );
}

export default VatSettingsCard;
